/**
 * Consultas de CEP e municipio feitas pelo NAVEGADOR (ViaCEP e IBGE). Nunca lancam: falha vira null / indisponivel.
 * Nenhum valor aqui e calculado: sao dados cadastrais (endereco e codigo IBGE).
 */

export type FetchFn = (url: string, init?: RequestInit) => Promise<Response>;

const TIMEOUT_MS = 4000;
const fetchPadrao: FetchFn = (url, init) => fetch(url, init);

export interface EnderecoCep {
  logradouro: string;
  bairro: string;
  municipio: string;
  uf: string;
  codigoIbge: string;
}

export interface MunicipioUf {
  id: number;
  nome: string;
}

export type ResultadoValidacaoMunicipio = { ok: true; codigoIbge: string } | { ok: false } | { indisponivel: true };

/** GET com timeout de 4 s; qualquer falha (rede, HTTP, JSON) vira undefined. */
async function obterJson(url: string, fetchFn: FetchFn): Promise<unknown> {
  const controle = new AbortController();
  const timer = setTimeout(() => controle.abort(), TIMEOUT_MS);
  try {
    const resp = await fetchFn(url, { signal: controle.signal, headers: { Accept: 'application/json' } });
    if (!resp.ok) return undefined;
    return await resp.json();
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

const ehObjeto = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const texto = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** CEP (com ou sem mascara) -> endereco. CEP inexistente, falha ou JSON ruim -> null. */
export async function buscarCep(cep: string, fetchFn: FetchFn = fetchPadrao): Promise<EnderecoCep | null> {
  const digitos = cep.replace(/\D/g, '');
  if (!/^\d{8}$/.test(digitos)) return null;
  const json = await obterJson(`https://viacep.com.br/ws/${digitos}/json/`, fetchFn);
  if (!ehObjeto(json) || json.erro === true || json.erro === 'true') return null;
  const municipio = texto(json.localidade);
  const uf = texto(json.uf).toUpperCase();
  const codigoIbge = texto(json.ibge);
  if (!municipio || !/^[A-Z]{2}$/.test(uf) || !/^\d{7}$/.test(codigoIbge)) return null;
  return { logradouro: texto(json.logradouro), bairro: texto(json.bairro), municipio, uf, codigoIbge };
}

/** Cache em memoria por UF (so respostas validas). */
const cacheMunicipios = new Map<string, MunicipioUf[]>();

/** Limpa o cache de municipios (uso em testes). */
export function limparCacheMunicipios(): void {
  cacheMunicipios.clear();
}

/** Municipios de uma UF pelo IBGE. Falha -> null ("indisponivel"). */
export async function municipiosDaUf(uf: string, fetchFn: FetchFn = fetchPadrao): Promise<MunicipioUf[] | null> {
  const sigla = uf.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(sigla)) return null;
  const emCache = cacheMunicipios.get(sigla);
  if (emCache) return emCache;
  const json = await obterJson(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${sigla}/municipios`, fetchFn);
  if (!Array.isArray(json)) return null;
  const lista: MunicipioUf[] = [];
  for (const item of json) {
    if (!ehObjeto(item) || typeof item.id !== 'number' || typeof item.nome !== 'string') return null;
    lista.push({ id: item.id, nome: item.nome });
  }
  cacheMunicipios.set(sigla, lista);
  return lista;
}

/** Sem acento e sem diferenca de caixa, para comparar nomes de municipio. */
export function chaveMunicipio(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Confere se o municipio existe na UF (IBGE). Comparacao sem acento e sem caixa.
 * Nao encontrado -> {ok:false}; IBGE indisponivel -> {indisponivel:true} (o cadastro aceita).
 */
export async function validarMunicipio(uf: string, nome: string, fetchFn: FetchFn = fetchPadrao): Promise<ResultadoValidacaoMunicipio> {
  const sigla = uf.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(sigla) || chaveMunicipio(nome) === '') return { ok: false };
  const municipios = await municipiosDaUf(sigla, fetchFn);
  if (municipios === null) return { indisponivel: true };
  const alvo = chaveMunicipio(nome);
  const achado = municipios.find((m) => chaveMunicipio(m.nome) === alvo);
  return achado ? { ok: true, codigoIbge: String(achado.id) } : { ok: false };
}
